import { IonButton, IonMenu, IonMenuToggle} from '@ionic/react';
import { menuController } from '@ionic/core/components';
import { FC, useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { createUseStyles } from 'react-jss';
import { HamburgerMenuIcon } from '../theme/Icons';
import { mediaMinWidth } from '../theme/utils';
import { LoadingPage } from '../LoadingPage';
import { AnonymousNavigationHeader, NavigationHeader } from './NavigationHeader';
import { NavigationContent } from './NavigationContent';
import { NavigationFooter } from './NavigationFooter';
import { NavigationMenuState } from './NavigationMenuState';
import { AppPrincipal, isAnonymousViewer } from '../auth/AppPrincipal';
import { ReminderBadge } from '../components/ReminderBadge';
import { IntercomState } from '../support/IntercomService';
import { useSubscription } from '@meraki-internal/state';
import { useBreakpoints } from '../theme/useBreakpoints';

const width = 375;
const useStyles = createUseStyles({
    menu: {
        '--max-width': `${width}px`,

        [mediaMinWidth.sm]: {
            '--min-width': `${width}px`,
        },

        '&::part(backdrop)': {
            backgroundColor: 'black',
            opacity: '0.55 !important'
        }
    }
});

type INavigationMenuToggle = {
    slot?: 'start' | 'end',
    noAppHeader?: boolean;
};

export const NavigationMenuToggle: FC<INavigationMenuToggle> = ({ slot, noAppHeader }) => {
    const intercomState = useInstance(IntercomState);
    useSubscription(() => intercomState);

    const { isAboveBreakpoint } = useBreakpoints();

    // on larger screens render placeholder (hamburger is shown in side nav instead)
    if (isAboveBreakpoint('lg')) {
        return <div slot={slot} style={{width: 50}} />;
    }

    const unreadIntercomCount = intercomState.state.unreadCount;
    let badgeStyle: any = {
        top: 12,
        left: 29,
        position: 'absolute',
        pointerEvents: 'none'
    };

    if (noAppHeader) {
        badgeStyle = {
            ...badgeStyle,
            top: 0,
            left: 28
        };
    }

    // this component will auto-hide if the corresponding menu is disabled
    // https://ionicframework.com/docs/api/menu-toggle
    return (
        <IonMenuToggle slot={slot} menu="navigation-menu">
            <IonButton fill="clear" id="navigation-menu-button" data-type="menu-nav-item">
                <HamburgerMenuIcon color="light" />
            </IonButton>
            {unreadIntercomCount > 0 &&
                <div style={badgeStyle}>
                    <ReminderBadge size="small" />
                </div>
            }
        </IonMenuToggle>
    );
};

export const NavigationMenu: FC<{contentId: string}> = ({ contentId }) => {
    const classes = useStyles();
    const navMenuState = useInstance(NavigationMenuState);

    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));

    const [deleting, setDeleting] = useState(false);

    // Close explicitly rather than clicking the menu-toggle button. Nav items close the menu AND route,
    // and menuController resolves a menu id asynchronously, so a toggle can land after the route swap has
    // already replaced this menu with a freshly mounted, closed one -- and would then OPEN it. Closing an
    // already-closed menu is a harmless no-op, so an explicit close is safe whichever side of the swap it lands.
    // Resolves once the slide-out animation has finished, so a caller that routes can await it and let the
    // menu finish closing -- routing away unmounts this menu, which mid-animation makes it vanish abruptly.
    const closeMenu = async () => {
        await menuController.close('navigation-menu');
    };

    if (deleting) {
        return (
            <LoadingPage showLogo />
        );
    }

    return (
        <IonMenu
            data-id="navigation-menu"
            menuId="navigation-menu"
            contentId={contentId}
            className={classes.menu}
            onIonWillOpen={() => navMenuState.setOpen(true)}
            onIonDidClose={() => navMenuState.setOpen(false)}
            swipeGesture={false}
        >
            {!isAnonymous && (
                <NavigationHeader
                    onDelete={() => setDeleting(true)}
                />
            )}
            {isAnonymous && (
                <AnonymousNavigationHeader />
            )}

            <NavigationContent
                closeMenuHandler={closeMenu}
            />

            <NavigationFooter closeMenu={closeMenu} />
        </IonMenu>
    );
};
